import { constants } from "../constant/constants";
import PrototypeInstance from "../helper/prototype-instance";
import { AzureStorageService } from "../helper/azure-storage-service";
import { HttpHelper } from "../helper/http-helper-service";
import { LocalStorageService } from "../helper/local-storage-service";
import {
  CX_DARK_THEME_ID,
  CX_LIGHT_THEME_ID,
  CxAppSettings,
  DEFAULT_LOGO_DARK,
  DEFAULT_LOGO_LIGHT,
  getCxAppLogoUrl,
  getCxSignInLogoUrl,
  normalizeCxAppSettings,
  resolveAccessibleLogoUrl,
} from "../model/cx-app-settings";

export type ApplyCxSettingsOptions = {
  /** Contact portal theme (post-login). */
  portalThemeId?: number;
  /** Use signInBackgroundTheme + logoLightUrl/logoDarkUrl (login screen). */
  useSignInBackgroundTheme?: boolean;
};
import { ServiceProviderSignUp } from "../model/serviceprovider/service-provider-model";
import { authService } from "./auth-service";
import axios from "axios";
const app = PrototypeInstance.getInstance();

const DEFAULT_APP_LOGO = "https://app.gorelo.io/assets/logo-fe96e8a1.svg";

function getFaviconType(faviconLink: string): string {
  const dataUrlType = faviconLink.match(/^data:([^;,]+)/i)?.[1];
  if (dataUrlType) {
    return dataUrlType;
  }

  const pathname = faviconLink.split("?")[0].split("#")[0].toLowerCase();
  if (pathname.endsWith(".ico")) return "image/x-icon";
  if (pathname.endsWith(".png")) return "image/png";
  if (pathname.endsWith(".svg")) return "image/svg+xml";
  if (pathname.endsWith(".jpg") || pathname.endsWith(".jpeg")) {
    return "image/jpeg";
  }
  if (pathname.endsWith(".gif")) return "image/gif";
  if (pathname.endsWith(".webp")) return "image/webp";

  return "";
}

export class CxAppSettingsService {
  public static async load() {
    let cxAppSettings = await CxAppSettingsService.getContactAppSettings();
    if (!cxAppSettings) {
      await this.loadDefaultSettings();
    } else {
      await this.loadSettingsFromWeb(cxAppSettings);
    }
  }

  public static async loadDefaultSettings() {
    let cxAppSettings = CxAppSettingsService.getDefaultCxAppSettings();
    await this.ApplySettings(cxAppSettings);
  }

  public static async loadFavIconAndTitle() {
    const model = new ServiceProviderSignUp.GetCXBrandingInfo(
      "gorelodev.z5.web.core.windows.net",
    );
    let mspCxSettings = await axios.post(
      constants.apiUrl.CommonUrls.getMspCXSetting,
      model,
    );
    const cxAppSettings = normalizeCxAppSettings(mspCxSettings?.data);
    if (cxAppSettings) {
      await this.setDomElements(cxAppSettings);
    } else {
      let defaultSettings = CxAppSettingsService.getDefaultCxAppSettings();
      await this.setDomElements(defaultSettings);
    }
  }

  public static async loadSettingsFromWeb(cxAppSettings: CxAppSettings) {
    const normalized = normalizeCxAppSettings(cxAppSettings) ?? cxAppSettings;
    await this.updateUrlsWithSasToken(normalized);
    LocalStorageService.setItem(
      constants.localStorage.keys.cxappsettings,
      JSON.stringify(normalized),
    );
    await this.ApplySettings(normalized, {
      portalThemeId: this.resolveThemeId(),
    });
  }

  /** SAS for authenticated portal blob logos (sidebar only; login flow unchanged). */
  public static async ensureLogoUrlAccessible(url: string): Promise<string> {
    if (!url?.trim() || this.urlHasSas(url)) {
      return url;
    }
    if (!authService.isAuthenticated()) {
      return url;
    }
    if (!url.includes("blob.core.windows.net")) {
      return url;
    }
    const sasToken = await AzureStorageService.generateSasToken();
    if (!sasToken) {
      return url;
    }
    return this.appendSasTokenIfNeeded(url, sasToken);
  }

  private static urlHasSas(url: string): boolean {
    return url.includes("sig=") || url.includes("?sv=") || url.includes("&sv=");
  }

  private static resolveThemeId(): number {
    const contact = app.config.globalProperties.$store?.getters?.[
      "ContactModule/getContact"
    ] as { themeId?: number } | null | undefined;
    return contact?.themeId ?? CX_DARK_THEME_ID;
  }

  private static appendSasTokenIfNeeded(url: string, sasToken: string): string {
    if (!url || this.urlHasSas(url)) {
      return url;
    }
    return url + sasToken;
  }

  private static async updateUrlsWithSasToken(cxAppSettings: CxAppSettings) {
    // SAS token API requires auth; on the login screen use blob URLs as returned by GetMspCXSetting.
    if (!authService.isAuthenticated()) {
      return;
    }

    const sasToken = await AzureStorageService.generateSasToken();
    cxAppSettings.appFavIconUrl = this.appendSasTokenIfNeeded(
      cxAppSettings.appFavIconUrl,
      sasToken,
    );
    cxAppSettings.appLogoUrl = this.appendSasTokenIfNeeded(
      cxAppSettings.appLogoUrl,
      sasToken,
    );
    if (cxAppSettings.logoLightUrl) {
      cxAppSettings.logoLightUrl = this.appendSasTokenIfNeeded(
        cxAppSettings.logoLightUrl,
        sasToken,
      );
    }
    if (cxAppSettings.logoDarkUrl) {
      cxAppSettings.logoDarkUrl = this.appendSasTokenIfNeeded(
        cxAppSettings.logoDarkUrl,
        sasToken,
      );
    }
  }

  public static async applyMspCxSettings(raw: unknown): Promise<void> {
    const cxAppSettings = normalizeCxAppSettings(raw);
    if (!cxAppSettings) {
      await this.loadDefaultSettings();
      return;
    }

    await this.updateUrlsWithSasToken(cxAppSettings);
    LocalStorageService.setItem(
      constants.localStorage.keys.cxappsettings,
      JSON.stringify(cxAppSettings),
    );
    await this.ApplySettings(cxAppSettings, { useSignInBackgroundTheme: true });
  }

  public static async ApplySettings(
    cxAppSettings: CxAppSettings,
    options: ApplyCxSettingsOptions = {},
  ) {
    const themedLogoUrl = options.useSignInBackgroundTheme
      ? getCxSignInLogoUrl(cxAppSettings)
      : getCxAppLogoUrl(
          cxAppSettings,
          options.portalThemeId ?? CX_DARK_THEME_ID,
        );
    const resolvedLogo = options.useSignInBackgroundTheme
      ? resolveAccessibleLogoUrl(themedLogoUrl, cxAppSettings.appLogoUrl)
      : themedLogoUrl;
    let appLogo =
      resolvedLogo && resolvedLogo !== ""
        ? resolvedLogo
        : options.portalThemeId === CX_LIGHT_THEME_ID
        ? DEFAULT_LOGO_LIGHT
        : DEFAULT_LOGO_DARK;
    let favIcon =
      cxAppSettings &&
      cxAppSettings.appFavIconUrl != "" &&
      cxAppSettings.appFavIconUrl != null
        ? cxAppSettings.appFavIconUrl
        : DEFAULT_APP_LOGO;
    await this.setDomElements(cxAppSettings);
    app.config.globalProperties.$store.commit(
      "MasterModule/SET_APP_ICON_LINK",
      appLogo,
    );
    app.config.globalProperties.$store.commit(
      "MasterModule/SET_APP_FAVICON_LINK",
      favIcon,
    );

    const showContactInfo = Boolean(
      cxAppSettings?.enableContactInfo ?? cxAppSettings?.showContactPreference,
    );
    app.config.globalProperties.$store.commit(
      "MasterModule/SET_SHOW_CONTACT_PREFERENCE",
      showContactInfo,
    );
    const showQuickConnect = Boolean(cxAppSettings?.isQuickConnectEnabled);
    app.config.globalProperties.$store.commit(
      "MasterModule/SET_SHOW_QUICK_CONNECT",
      showQuickConnect,
    );
    app.config.globalProperties.$store.commit(
      "Set_ServiceProviderId",
      cxAppSettings && cxAppSettings.serviceProviderId,
    );

    if (showContactInfo) {
      app.config.globalProperties.$store.commit(
        "Set_ServiceProviderName",
        cxAppSettings?.organizationName?.trim() || "",
      );
      app.config.globalProperties.$store.commit(
        "Set_SupportEmail",
        cxAppSettings?.supportEmail?.trim() || "",
      );
      app.config.globalProperties.$store.commit(
        "Set_ServiceProviderPhone",
        cxAppSettings?.supportPhone?.trim() || "",
      );
    } else {
      app.config.globalProperties.$store.commit("Set_ServiceProviderName", "");
      app.config.globalProperties.$store.commit("Set_SupportEmail", "");
      app.config.globalProperties.$store.commit("Set_ServiceProviderPhone", "");
    }
  }

  private static async setDomElements(cxAppSettings: CxAppSettings) {
    let appTitle =
      cxAppSettings &&
      cxAppSettings.appTitle != "" &&
      cxAppSettings.appTitle != null
        ? cxAppSettings.appTitle
        : "Customer Experience Application";
    let appFavIcon =
      cxAppSettings &&
      cxAppSettings.appFavIconUrl != "" &&
      cxAppSettings.appFavIconUrl != null
        ? cxAppSettings.appFavIconUrl
        : DEFAULT_APP_LOGO;
    document.title = appTitle;
    await this.setDomFaviconLink(appFavIcon);
  }

  private static async setDomFaviconLink(faviconLink: string) {
    const href = faviconLink?.trim() || DEFAULT_APP_LOGO;
    const resolvedHref = new URL(href, document.baseURI).href;

    document
      .querySelectorAll<HTMLLinkElement>("link[rel~='icon']")
      .forEach((existingLink) => existingLink.remove());

    const link = document.createElement("link");
    const type = getFaviconType(resolvedHref);

    link.rel = "icon";
    if (type) {
      link.type = type;
    }
    if (type === "image/svg+xml") {
      link.setAttribute("sizes", "any");
    }
    link.href = resolvedHref;
    document.head.appendChild(link);
  }

  private static async getContactAppSettings(): Promise<
    CxAppSettings | undefined
  > {
    let cxAppSettings = normalizeCxAppSettings(
      await HttpHelper.getData(
        constants.apiUrl.serviceProviderUrls.getCXAppConfiguration,
      ),
    );
    if (cxAppSettings) {
      LocalStorageService.setItem(
        constants.localStorage.keys.cxappsettings,
        JSON.stringify(cxAppSettings),
      );
    } else {
      try {
        const cxAppSettingsString = LocalStorageService.getItem(
          constants.localStorage.keys.cxappsettings,
        );
        if (cxAppSettingsString) {
          cxAppSettings =
            normalizeCxAppSettings(JSON.parse(cxAppSettingsString)) ??
            undefined;
        }
      } catch (ex) {
        console.error(ex);
      }
    }
    return cxAppSettings ?? undefined;
  }

  private static getDefaultCxAppSettings() {
    return new CxAppSettings(
      -1,
      "Customer Experience Application",
      "",
      DEFAULT_APP_LOGO,
      0,
      false,
    );
  }

  public static async ApplyCxAppSettings(cxAppSettings: CxAppSettings) {
    const normalized = normalizeCxAppSettings(cxAppSettings) ?? cxAppSettings;
    document.title = normalized.appTitle || "Customer Experience Application";
    await this.setDomFaviconLink(normalized.appFavIconUrl);

    app.config.globalProperties.$store.commit(
      "MasterModule/SET_APP_ICON_LINK",
      getCxAppLogoUrl(normalized, this.resolveThemeId()) ||
        normalized.appLogoUrl,
    );
    app.config.globalProperties.$store.commit(
      "MasterModule/SET_SERVICE_PROVIDER_ID",
      normalized.serviceProviderId,
    );
  }
}
